import type { PostSortRowsParams } from "ag-grid-enterprise";
import type { DsmScopeItem } from "design-scope";

/** Extra print rows carry the source item so filters still match, plus which link to show. */
export type DesignScopePrintRow = DsmScopeItem & {
  printPackageIndex?: number;
  printSourceId?: string;
};

export function isDesignScopePrintContinuation(
  data?: DsmScopeItem | null
): boolean {
  return (
    ((data as DesignScopePrintRow | undefined)?.printPackageIndex ?? 0) > 0
  );
}

export function displayedDesignPackageIndex(
  data?: DsmScopeItem | null
): number {
  return (data as DesignScopePrintRow | undefined)?.printPackageIndex ?? 0;
}

export function designScopePrintPackageRowId(
  itemId: string,
  commitmentIndex: number
): string {
  return `${itemId}::design-package::${commitmentIndex}`;
}

/**
 * Print lists one design package per row. The scope item row keeps the first
 * link; each further link is the next row and shares the source item's fields
 * so an active filter does not drop it.
 */
export function expandDesignScopePrintRows(
  items: DsmScopeItem[]
): DsmScopeItem[] {
  const rows: DsmScopeItem[] = [];
  for (const item of items) {
    rows.push(item);
    item.commitments.forEach((commitment, index) => {
      if (index === 0 || !commitment.designPackageId) return;
      const row: DesignScopePrintRow = {
        ...item,
        id: designScopePrintPackageRowId(item.id, index),
        printPackageIndex: index,
        printSourceId: item.id
      };
      rows.push(row);
    });
  }
  return rows;
}

/** Keep each extra design-package row directly under its scope item after sorting. */
export function keepPrintContinuationRowsWithSource(
  params: PostSortRowsParams<DsmScopeItem>
) {
  const extras = new Map<string, typeof params.nodes>();
  const rest: typeof params.nodes = [];
  for (const node of params.nodes) {
    const sourceId = (node.data as DesignScopePrintRow | undefined)
      ?.printSourceId;
    if (!sourceId) {
      rest.push(node);
      continue;
    }
    const list = extras.get(sourceId) ?? [];
    list.push(node);
    extras.set(sourceId, list);
  }
  if (extras.size === 0) return;
  const next: typeof params.nodes = [];
  for (const node of rest) {
    next.push(node);
    const id = node.data?.id;
    const attached = id ? extras.get(id) : undefined;
    if (id && attached) {
      next.push(...attached);
      extras.delete(id);
    }
  }
  params.nodes.splice(0, params.nodes.length, ...next);
}
